import type { CaptureProfile } from '@/domain/types'

export type GameDetectionFailureReason = 'user-cancelled' | 'source-replaced' | 'disposed' | 'reload'
  | 'capture-ended' | 'dimensions-changed' | 'profile-changed' | 'timeout' | 'ambiguous' | 'probe-error' | 'unknown' | 'unconfirmed'
export type CapturePreparationEntry = 'selection' | 'session-start' | 'first-use-calibration' | 'calibration' | 'ocr-retry'

export class GameDetectionPreparationError extends Error {
  constructor(readonly reason: GameDetectionFailureReason) {
    super(reason)
    this.name = 'GameDetectionPreparationError'
  }
}

export function silentGameDetectionFailure(reason: GameDetectionFailureReason): boolean {
  return ['user-cancelled', 'source-replaced', 'disposed', 'reload', 'capture-ended'].includes(reason)
}

export function captureProfileIdentity(profile: CaptureProfile | null): string {
  return JSON.stringify(profile && [profile.id, profile.presetId, profile.presetVersion,
    profile.layoutVersion, profile.sourceWidth, profile.sourceHeight])
}

export interface CapturePreparationDiagnostic {
  selectionId: number
  entry: CapturePreparationEntry
  startedAt: number
  elapsedMs: number
  outcome: 'preparing' | 'ready' | 'unknown' | 'unconfirmed' | 'skipped' | 'cancelled' | 'error' | 'fallback' | 'needs-choice'
  resolution?: 'automatic' | 'saved-profile' | 'manual'
  reason: GameDetectionFailureReason | null
  stages: { pickerExitMs: number | null; gameDetectionMs: number | null }
  pickerExitFallback: boolean | null
  sampleCount: number
  initialDimensions: { width: number; height: number }
  currentDimensions: { width: number; height: number }
  initialVisibility: DocumentVisibilityState
  currentVisibility: DocumentVisibilityState
  profileIdentityChanged: boolean
}

/** Chromium can suspend animation frames while the game covers the page. */
export function waitForNativePickerExit(): Promise<{ elapsedMs: number; fallback: boolean }> {
  const startedAt = Date.now()
  return new Promise(resolve => {
    let frame: number | undefined
    let delay: number | undefined
    let settled = false
    const finish = (fallback: boolean) => {
      if (settled) return
      settled = true
      window.clearTimeout(deadline)
      window.clearTimeout(delay)
      if (frame !== undefined) window.cancelAnimationFrame?.(frame)
      resolve({ elapsedMs: Date.now() - startedAt, fallback })
    }
    const deadline = window.setTimeout(() => finish(true), 250)
    if (document.visibilityState === 'hidden' || typeof requestAnimationFrame !== 'function') {
      delay = window.setTimeout(() => finish(true), 0)
      return
    }
    frame = requestAnimationFrame(() => {
      frame = requestAnimationFrame(() => {
        frame = undefined
        delay = window.setTimeout(() => finish(false), 100)
      })
    })
  })
}
